// Dependency-free export helpers for the Synthesis Visualizations module.
//
// The module styles everything INLINE, so a node's outerHTML carries its full appearance into a
// fresh print window — no html2canvas / jsPDF needed (and the spec forbids adding a chart library).
// PDF is the browser's native "Save as PDF" from the print dialog; true PNG export would need a
// canvas library and is deferred. The module-level Export downloads the exact synthesis JSON — a
// real artifact — while per-tab Export prints just that tab for a client-ready page.

export function downloadJSON(data: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string
  ));
}

// Open a clean print window containing just `node`'s rendered HTML (inline styles carry over) and
// invoke the browser print dialog → the consultant saves it as a PDF. The trigger button lives
// OUTSIDE the node, so it never appears in the exported page.
export function printNode(node: HTMLElement | null, title: string) {
  if (!node) return;
  const w = window.open('', '_blank', 'width=940,height=1000');
  if (!w) return; // pop-up blocked — nothing to clean up
  w.document.open();
  w.document.write(
    '<!doctype html><html><head><meta charset="utf-8">'
    + `<title>${escapeHtml(title)}</title>`
    + '<style>'
    + 'body{margin:28px;background:#fff;color:#0f172a;'
    + 'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;}'
    + '*{box-sizing:border-box;}'
    + '@media print{body{margin:12mm;}}'
    + '</style></head><body>'
    + `<h2 style="font-size:13px;letter-spacing:.02em;color:#64748b;margin:0 0 18px;">${escapeHtml(title)}</h2>`
    + node.outerHTML
    + '</body></html>',
  );
  w.document.close();
  w.focus();
  // Let the new document lay out before invoking print; if it's blocked the user can print manually.
  setTimeout(() => { try { w.print(); } catch { /* manual print still available */ } }, 350);
}
